/**
 * Custom API functions for reindexing Knowledge Base and Projects.
 * These are not auto-generated by orval.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { customInstance } from "~/custom-instance";

const API_BASE_URL = import.meta.env.VITE_BASE_API_URL;
if (!API_BASE_URL) {
  throw new Error("VITE_BASE_API_URL environment variable is not configured");
}

export interface ReindexResponse {
  job_id: string;
  message: string;
  status: string;
  total_documents: number;
  case_id: string;
  tenant_id: string;
}

export interface IndexStatusResponse {
  case_id: string;
  tenant_id: string;
  pgvector_indexed: boolean;
  indexed_at: string | null;
  index_status: string; // pending, indexing, indexed, completed, failed, not_found
  documents_loaded: number;
  // Progress tracking for active reindex jobs
  total_jobs: number;
  completed_jobs: number;
  failed_jobs: number;
  progress_percentage: number;
  active_job_id: string | null;
  error_message: string | null;
}

/**
 * Reindex the knowledge base for an organization.
 * This triggers sky-rag to re-process all knowledge documents with pgVector.
 */
export const reindexKnowledgeBase = (organizationId: string) => {
  return customInstance<ReindexResponse>({
    url: `${API_BASE_URL}/organizations/${organizationId}/knowledge/reindex`,
    method: "POST",
  });
};

/**
 * Reindex a specific project/case.
 * This triggers sky-rag to re-process all documents in the project with pgVector.
 */
export const reindexProject = (organizationId: string, projectId: string) => {
  return customInstance<ReindexResponse>({
    url: `${API_BASE_URL}/organizations/${organizationId}/projects/${projectId}/reindex`,
    method: "POST",
  });
};

/**
 * Get the index status for a project.
 */
export const getProjectIndexStatus = (
  organizationId: string,
  projectId: string
) => {
  return customInstance<IndexStatusResponse>({
    url: `${API_BASE_URL}/organizations/${organizationId}/projects/${projectId}/index-status`,
    method: "GET",
  });
};

/**
 * Get the index status for the knowledge base.
 * Knowledge base uses case_id = "knowledge-base-pages"
 */
export const getKnowledgeBaseIndexStatus = (organizationId: string) => {
  return customInstance<IndexStatusResponse>({
    url: `${API_BASE_URL}/organizations/${organizationId}/projects/knowledge-base-pages/index-status`,
    method: "GET",
  });
};

// React Query hooks

export const useReindexKnowledgeBase = () => {
  return useMutation({
    mutationFn: (organizationId: string) =>
      reindexKnowledgeBase(organizationId),
  });
};

export const useReindexProject = () => {
  return useMutation({
    mutationFn: ({
      organizationId,
      projectId,
    }: {
      organizationId: string;
      projectId: string;
    }) => reindexProject(organizationId, projectId),
  });
};

export const useProjectIndexStatus = (
  organizationId: string,
  projectId: string,
  options?: { enabled?: boolean; refetchOnIndexing?: boolean }
) => {
  const query = useQuery({
    queryKey: ["projectIndexStatus", organizationId, projectId],
    queryFn: () => getProjectIndexStatus(organizationId, projectId),
    enabled: options?.enabled ?? true,
    // Refetch every 5 seconds when indexing is in progress
    refetchInterval: (query) => {
      const status = query.state.data?.index_status;
      if (options?.refetchOnIndexing !== false && status === "indexing") {
        return 5000;
      }
      return false;
    },
  });
  return query;
};

export const useKnowledgeBaseIndexStatus = (
  organizationId: string,
  options?: { enabled?: boolean; refetchOnIndexing?: boolean }
) => {
  const query = useQuery({
    queryKey: ["knowledgeBaseIndexStatus", organizationId],
    queryFn: () => getKnowledgeBaseIndexStatus(organizationId),
    enabled: options?.enabled ?? true,
    // Refetch every 5 seconds when indexing is in progress
    refetchInterval: (query) => {
      const status = query.state.data?.index_status;
      if (options?.refetchOnIndexing !== false && status === "indexing") {
        return 5000;
      }
      return false;
    },
  });
  return query;
};
